import { useState } from "react"

import { clsx } from "clsx"

import { formatCurrency } from "~/utils/finance/format"

import type {
  IncentivesStepBundle,
  PricingIncentive,
  PricingIncentivePayee,
  PricingIncentiveSelectionChange,
  PricingKindBundle,
  PricingPayeeOption,
  PricingSellerIncentivePayee,
} from "./pricing-kind-bundle"
import {
  PricingLoadError,
  PricingLoading,
  PricingRequiredNotice,
  PricingStepCloseFooter,
  PricingStepDrawer,
  PricingStepFooter,
  ToggleCard,
  ToggleCardGrid,
  ToggleCardTitle,
} from "./pricing-step-chrome"
import { usePricingSaveGuard } from "./use-pricing-save-guard"

export const INCENTIVES_PRICING_REQUIRED = `Incentives need current System Pricing.`

const EMPTY_SELECTION_CHANGES: readonly PricingIncentiveSelectionChange[] = []

// Amounts are the server's, shown as returned.
const amountText = (amount: number | null): string | null =>
  amount === null ? null : formatCurrency(amount)

const incentiveSelectionChangeNotice = (
  change: PricingIncentiveSelectionChange,
): string => {
  const previous = amountText(change.previousAmount)
  const next = amountText(change.amount)
  if (change.change === `removed`) {
    return previous
      ? `${change.incentiveName} (${previous}) no longer applies to this proposal and was removed`
      : `${change.incentiveName} no longer applies to this proposal and was removed`
  }
  return previous && next
    ? `${change.incentiveName} changed from ${previous} to ${next}`
    : `${change.incentiveName} changed value${next ? ` to ${next}` : ``}`
}

/** Selected incentives revalidation removed or revalued, until the next save acknowledges them. */
const SelectionChangeNotices = ({
  changes,
}: {
  changes: readonly PricingIncentiveSelectionChange[]
}): JSX.Element | null =>
  changes.length > 0 ? (
    // Foreground text on the gold tint: gold text is unreadable on the light theme.
    <div
      role="status"
      data-testid="incentive-selection-changes"
      className="mb-4 rounded-md border border-bp-gold/60 bg-bp-gold/15 px-3 py-2 text-[length:var(--step--2)] text-foreground"
    >
      <ul className="flex list-none flex-col gap-1">
        {changes.map((change) => (
          <li key={change.id} className="tabular-nums">
            {incentiveSelectionChangeNotice(change)}
          </li>
        ))}
      </ul>
    </div>
  ) : null

const isSellerPayee = (
  payee: PricingIncentivePayee | undefined,
): payee is PricingSellerIncentivePayee =>
  payee === `homeowner` || payee === `powur`

/** Selection and payee edits, kept together so Save writes them as one. */
type Draft = {
  selected: ReadonlySet<string>
  payees: ReadonlyMap<string, PricingIncentivePayee>
  /** Incentives whose payee the seller chose in this draft; only these are sent. */
  editedPayees: ReadonlySet<string>
}

const displayName = (incentive: PricingIncentive): string =>
  incentive.customDisplayName?.trim() || incentive.incentiveName

// The saved Proposal Incentive payee, never the Admin default, seeds the draft.
const savedDraftOf = (incentives: readonly PricingIncentive[]): Draft => ({
  selected: new Set(incentives.filter((i) => i.selected).map((i) => i.id)),
  payees: new Map(
    incentives.flatMap((i) =>
      i.incentivePayee ? [[i.id, i.incentivePayee] as const] : [],
    ),
  ),
  editedPayees: new Set(),
})

const PayeeOptions = ({
  incentive,
  options,
  payee,
  disabled,
  onChange,
}: {
  incentive: PricingIncentive
  options: readonly PricingPayeeOption[]
  payee: PricingIncentivePayee | undefined
  disabled: boolean
  onChange: (payee: PricingSellerIncentivePayee) => void
}): JSX.Element => (
  // Clicks here choose a payee; they must not reach the card's toggle.
  <fieldset
    onClick={(event) => event.stopPropagation()}
    className="flex flex-col gap-2"
  >
    <legend className="sr-only">{`Payee for ${displayName(incentive)}`}</legend>
    {options.map((option) => {
      const checked = payee === option.value
      return (
        <div key={option.value} className="flex flex-col gap-1">
          <label
            className={clsx(
              `flex items-center gap-2.5 rounded-md border px-3 py-2 text-[length:var(--step--1)] text-foreground transition-colors`,
              checked
                ? `border-bp-accent bg-bp-accent/10`
                : `border-bp-border bg-bp-elevated`,
              disabled
                ? `cursor-default`
                : `cursor-pointer hover:border-bp-accent`,
            )}
          >
            <input
              type="radio"
              name={`incentive-payee-${incentive.id}`}
              className="size-4 accent-bp-accent"
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            {option.label}
          </label>
          {option.note ? (
            <p className="px-1 text-[length:var(--step--2)] leading-snug text-muted">
              {option.note}
            </p>
          ) : null}
        </div>
      )
    })}
  </fieldset>
)

const IncentiveCard = ({
  incentive,
  payeeOptions,
  selected,
  payee,
  disabled,
  onToggle,
  onPayeeChange,
}: {
  incentive: PricingIncentive
  payeeOptions: readonly PricingPayeeOption[]
  selected: boolean
  payee: PricingIncentivePayee | undefined
  disabled: boolean
  onToggle: () => void
  onPayeeChange: (payee: PricingSellerIncentivePayee) => void
}): JSX.Element => (
  <ToggleCard
    selected={selected}
    disabled={disabled}
    onToggle={onToggle}
    title={<ToggleCardTitle>{displayName(incentive)}</ToggleCardTitle>}
  >
    {incentive.summary ? (
      <p className="text-[length:var(--step--2)] leading-snug text-foreground-soft">
        {incentive.summary}
      </p>
    ) : null}
    {incentive.requirements ? (
      <p className="text-[length:var(--step--2)] leading-snug text-muted">
        <span className="font-semibold text-foreground-soft">Requirements:</span>
        {` `}
        {incentive.requirements}
      </p>
    ) : null}
    {selected ? (
      <PayeeOptions
        incentive={incentive}
        options={payeeOptions}
        payee={payee}
        disabled={disabled}
        onChange={onPayeeChange}
      />
    ) : null}
    <span className="self-end text-[length:var(--step-0)] font-semibold text-bp-accent tabular-nums">
      {formatCurrency(incentive.value)}
    </span>
  </ToggleCard>
)

const IncentivesCatalog = ({
  bundle,
  proposalId,
  incentives,
  refreshedAt,
  selectionChanges,
  onClose,
}: {
  bundle: IncentivesStepBundle
  proposalId: string
  incentives: readonly PricingIncentive[]
  refreshedAt: string | null
  selectionChanges: readonly PricingIncentiveSelectionChange[]
  onClose: () => void
}): JSX.Element => {
  const saveIncentives = bundle.useSave(proposalId)
  const payeeOptions = bundle.usePayeeOptions(proposalId)
  const guard = usePricingSaveGuard({ onClose, savedFeedback: true })
  // Draft lives only here; unmounting (any close path) discards it.
  const [draft, setDraft] = useState<Draft | null>(null)
  const current = draft ?? savedDraftOf(incentives)
  const { selected, payees } = current
  const [refreshing, setRefreshing] = useState(false)
  const saving = saveIncentives.isLoading
  const locked = saving || guard.justSaved || refreshing
  const { Toolbar } = bundle

  const toggle = (id: string): void => {
    saveIncentives.reset()
    const next = new Set(selected)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setDraft({ ...current, selected: next })
  }

  const choosePayee = (id: string, payee: PricingSellerIncentivePayee): void => {
    saveIncentives.reset()
    setDraft({
      ...current,
      payees: new Map(payees).set(id, payee),
      editedPayees: new Set(current.editedPayees).add(id),
    })
  }

  const submit = (): void => {
    if (locked || !guard.start()) return
    saveIncentives.run(
      {
        selectedIncentiveIds: incentives
          .filter((incentive) => selected.has(incentive.id))
          .map((incentive) => incentive.id),
        // Only payees the seller chose in this draft, so neither a drawer
        // opened earlier nor a refetch mid-draft can overwrite a payee
        // saved elsewhere.
        incentivePayees: incentives.flatMap((incentive) => {
          const payee = payees.get(incentive.id)
          return selected.has(incentive.id) &&
            current.editedPayees.has(incentive.id) &&
            isSellerPayee(payee)
            ? [{ incentiveId: incentive.id, incentivePayee: payee }]
            : []
        }),
        // Drains only the notices this step read, so a change recorded while
        // it was open survives the save. Omitted when none were shown.
        ...(selectionChanges.length > 0
          ? {
              acknowledgedIncentiveChangeIds: selectionChanges.map(
                (change) => change.id,
              ),
            }
          : {}),
      },
      {
        onSettled: guard.release,
        onSuccess: () => {
          setDraft(null)
          guard.succeed()
        },
      },
    )
  }

  return (
    <>
      <SelectionChangeNotices changes={selectionChanges} />
      {Toolbar ? (
        <Toolbar
          proposalId={proposalId}
          refreshedAt={refreshedAt}
          disabled={locked}
          onRefreshed={() => {
            saveIncentives.reset()
            setDraft(null)
          }}
          onRefreshingChange={setRefreshing}
        />
      ) : null}
      {incentives.length === 0 ? (
        <p role="status" className="text-[length:var(--step--1)] text-muted">
          {bundle.copy.empty}
        </p>
      ) : (
        <ToggleCardGrid testId="incentives-cards-container">
          {incentives.map((incentive) => (
            <IncentiveCard
              key={incentive.id}
              incentive={incentive}
              payeeOptions={payeeOptions}
              selected={selected.has(incentive.id)}
              payee={payees.get(incentive.id)}
              disabled={locked}
              onToggle={() => toggle(incentive.id)}
              onPayeeChange={(payee) => choosePayee(incentive.id, payee)}
            />
          ))}
        </ToggleCardGrid>
      )}

      <PricingStepFooter
        onClose={onClose}
        error={
          saveIncentives.isError
            ? {
                message: bundle.describeSaveError(saveIncentives.error),
                onRetry: submit,
              }
            : null
        }
        savedMessage={guard.justSaved ? `Incentives saved` : null}
        save={{
          label: `Save incentives`,
          saving,
          disabled: locked,
          onSave: submit,
        }}
      />
    </>
  )
}

/**
 * Incentives: values come from stored System Pricing, so the step waits for
 * it. Selections and payees are a draft written together on Save.
 */
export const PricingIncentivesStep = ({
  kind,
  proposalId,
  onClose,
}: {
  kind: PricingKindBundle
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const bundle = kind.incentives
  const pricing = kind.usePricingStatus(proposalId)
  const { hasPricing } = pricing
  const incentivesQuery = bundle.useIncentives(proposalId, {
    enabled: hasPricing,
  })
  // The read can still refuse for missing pricing (pricing cleared since the
  // status loaded); that is the gate, not a failure.
  const pricingRequired =
    (pricing.isSuccess && !hasPricing) ||
    (incentivesQuery.isError &&
      bundle.isPricingRequiredError(incentivesQuery.error))
  const loading = pricing.isLoading || (hasPricing && incentivesQuery.isLoading)
  const loadError =
    pricing.isError || (incentivesQuery.isError && !pricingRequired)
  const catalog =
    hasPricing && incentivesQuery.isSuccess ? incentivesQuery.data : undefined

  return (
    <PricingStepDrawer title="Incentives" onClose={onClose}>
      {loading ? <PricingLoading>Loading incentives</PricingLoading> : null}
      {loadError ? (
        <PricingLoadError
          message="Unable to load incentives"
          onRetry={() => {
            if (pricing.isError) void pricing.refetch()
            if (incentivesQuery.isError) void incentivesQuery.refetch()
          }}
        />
      ) : null}
      {pricingRequired ? (
        <PricingRequiredNotice
          message={INCENTIVES_PRICING_REQUIRED}
          detail={bundle.copy.pricingRequiredDetail}
          systemPricingPath={kind.systemPricingPath(proposalId)}
        />
      ) : null}

      {catalog ? (
        <IncentivesCatalog
          bundle={bundle}
          proposalId={proposalId}
          incentives={catalog.incentives}
          refreshedAt={catalog.refreshedAt ?? null}
          selectionChanges={
            catalog.incentiveSelectionChanges ?? EMPTY_SELECTION_CHANGES
          }
          onClose={onClose}
        />
      ) : (
        <PricingStepCloseFooter onClose={onClose} />
      )}
    </PricingStepDrawer>
  )
}
